import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import BudgetTileRow from "@/components/ui/v1/BudgetTileRow";
import PostpaidBudgetTileRow from "@/components/ui/v1/PostpaidBudgetTileRow";
import DataTable from "@/components/ui/v1/table/DataTable";
import Table from "@/components/ui/v1/table/Table";
import { MonthYearSelector } from "@/components/ui/v2/month-year-selector";
import { useCampaignJobBoardMonths } from "@/features/campaigns/jobboards/api/useCampaignJobBoardMonths";
import { ChannelProvider } from "@/lib/contexts/Channel/ChannelsProvider";
import { useChannels } from "@/lib/contexts/Channel/useChannels";
import { ClientBudgetMonthProvider } from "@/lib/contexts/ClientBudget/ClientBudgetMonthProvider";
import { useClientBudgetMonth } from "@/lib/contexts/ClientBudget/useClientBudgetMonth";
import { useClients } from "@/lib/hooks/useClients";
import { useDateConstraints } from "@/lib/hooks/useDateConstraints";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { usePopup } from "@/lib/hooks/usePopup";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";
import { useBudgetPlanningTableColumns } from "@/lib/tableColumns/useBudgetPlanningTableColumns";
import { useChannelBudgetPlanningTableColumns } from "@/lib/tableColumns/useChannelBudgetPlanningTableColumns";
import { PaymentTypeEnum } from "@/types/client/ClientType";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";
import { CampaignTypeDTO } from "@/types/response/CampaignType";
import { ChannelType } from "@/types/response/ChannelType";

interface BudgetPlanningContentProps {
  campaignId?: string;
  clientId: string;
  descending?: boolean;
  month: string;
  page: number;
  search?: string;
  year: string;
  orderBy?: string;
}

const BudgetPlanningContent = ({
  campaignId,
  clientId,
  descending,
  month,
  page,
  year,
  orderBy,
  pageSize,
}: BudgetPlanningContentProps & { pageSize: number }) => {
  const { budgetPlanningTableColumns, budgetPlanningJobBoardColumns } =
    useBudgetPlanningTableColumns(month, year);
  const {
    data: campaignJobBoardMonths,
    isLoading,
    isFetching,
  } = useCampaignJobBoardMonths({
    clientId,
    descending,
    month,
    orderBy,
    page,
    pageSize,
    year,
  });
  const { channelTableColumns } = useChannelBudgetPlanningTableColumns(
    clientId,
    month,
    year
  );
  const { client } = useClients();
  const { clientChannels, clientChannelsLoading } = useChannels();
  const { handleSort } = useSortSearchParams();
  const { maxDate } = useDateConstraints();
  const { resetPopup } = usePopup();
  const { setPage } = usePageSearchParams();

  const { setPageSize } = usePageSizeSearchParams();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clientBudgetMonth, isFetching: clientBudgetMonthFetching } =
    useClientBudgetMonth();

  const [monthlyBudget, setMonthlyBudget] = useState<number | undefined>(
    client?.monthlyBudget
  );

  useEffect(() => {
    if (client?.monthlyBudget !== undefined) {
      setMonthlyBudget(client.monthlyBudget);
    }
  }, [client?.monthlyBudget]);

  const handleToggleRow = (row: CampaignTypeDTO, isExpanding: boolean) => {
    if (isExpanding) {
      // If expanding, navigate to the specific campaign row
      navigate(
        campaignId
          ? `../${row.id}?${searchParams.toString()}`
          : `${row.id}?${searchParams.toString()}`,
        { relative: "path" }
      );
    } else {
      // If collapsing, navigate back to budget planning list
      navigate(
        campaignId
          ? `..?${searchParams.toString()}`
          : `.?${searchParams.toString()}`,
        { relative: "path" }
      );
    }
  };
  const handleSuccess = () => {
    resetPopup();
  };

  return (
    <ModernPageLayout title={t("common.budgetPlanning")}>
      {client?.paymentType === PaymentTypeEnum.SUBSCRIPTION ? (
        <BudgetTileRow
          allocatedBudget={client?.allocatedBudget}
          availableBudget={client?.allocableBudgetWithoutOverspend}
          clientId={clientId}
          currentBudget={client?.currentBudget}
          monthlyBudget={monthlyBudget}
          onEditSuccess={handleSuccess}
        />
      ) : (
        <PostpaidBudgetTileRow
          allocableBudget={clientBudgetMonth?.allocableBudget}
          allocatedBudget={clientBudgetMonth?.allocatedBudget}
          budgetCap={clientBudgetMonth?.budgetCap}
          clientId={clientId}
          isLoading={clientBudgetMonthFetching}
          month={month}
          onEditSuccess={handleSuccess}
          paymentType={client?.paymentType}
          spent={clientBudgetMonth?.spent}
          year={year}
        />
      )}
      <div className="content-section flex flex-col gap-4 items-end">
        <MonthYearSelector
          maxDate={maxDate}
          value={[year, month]}
          onChange={(yearMonth) => {
            const [newYear, newMonth] = yearMonth;
            // Navigate relative to budget-planning base, accounting for current depth
            const levelsUp = campaignId ? "../../../" : "../../";
            const targetPath = `${levelsUp}${newYear}/${newMonth}${
              campaignId ? `/${campaignId}` : ""
            }`;
            navigate(targetPath, { relative: "path" });
          }}
        />
      </div>
      <div className="flex items-center justify-between min-h-12">
        <div>
          <h1 className="text-lg font-semibold text-foreground">
            {t("common.campaigns")}
          </h1>
        </div>
      </div>

      <DataTable<CampaignTypeDTO, JobboardCampaignType>
        columns={budgetPlanningTableColumns}
        data={campaignJobBoardMonths?.data || []}
        direction={descending ? "desc" : "asc"}
        entityName="campaignJobBoardMonths"
        getNestedData={(row) =>
          row.integrations?.reduce(
            (acc: JobboardCampaignType[], integration) => {
              if (!integration.selectedMonth) return acc;
              const cimId =
                integration.selectedMonth.clientIntegrationModuleId ??
                integration.clientIntegrationModuleId;
              const nested = { ...integration.selectedMonth, clientIntegrationModuleId: cimId };
              return [...acc, nested];
            },
            []
          ) ?? []
        }
        idField={"id"}
        isFetching={isFetching}
        isLoading={isLoading}
        itemsPerPage={pageSize}
        nestedColumns={budgetPlanningJobBoardColumns}
        onSort={handleSort}
        orderBy={orderBy ?? undefined}
        onToggleRow={handleToggleRow}
        openedRowId={campaignId}
        page={page}
        setPage={setPage}
        setItemsPerPage={setPageSize}
        totalItems={campaignJobBoardMonths?.totalRecords || 0}
      />

      <div className="flex items-center justify-between min-h-12">
        <div>
          <h1 className="text-lg font-semibold text-foreground">
            {t("common.channels")}
          </h1>
        </div>
      </div>

      <Table<ChannelType>
        columns={channelTableColumns}
        data={clientChannels?.data || []}
        isLoading={clientChannelsLoading}
        onSort={handleSort}
        direction={descending ? "desc" : "asc"}
      />
    </ModernPageLayout>
  );
};

const BudgetPlanningPage = () => {
  const { clientId, month, year, campaignId } = useParams();
  const { orderBy, descending } = useSortSearchParams();
  const { page } = usePageSearchParams();
  const { pageSize } = usePageSizeSearchParams();
  const { isDateValid, nextMonthFormatted, nextMonthYearFormatted } =
    useDateConstraints();
  if (!clientId || !month || !year) return null;

  // Convert URL params to numbers for comparison
  const selectedYear = Number.parseInt(year, 10);
  const selectedMonth = Number.parseInt(month, 10);

  // Check if selected date is valid (not beyond next month)
  if (!isDateValid(selectedYear, selectedMonth)) {
    const levelsUp = campaignId ? "../../../" : "../../";
    return (
      <Navigate
        to={`${levelsUp}${nextMonthYearFormatted}/${nextMonthFormatted}`}
        replace
        relative="path"
      />
    );
  }

  return (
    <ChannelProvider
      clientId={clientId}
      descending={descending}
      month={month}
      orderBy={orderBy}
      page={page}
      pageSize={pageSize}
      year={year}
    >
      <ClientBudgetMonthProvider clientId={clientId} month={month} year={year}>
        <BudgetPlanningContent
          campaignId={campaignId}
          clientId={clientId}
          descending={descending}
          month={month}
          page={page}
          year={year}
          orderBy={orderBy}
          pageSize={pageSize}
        />
      </ClientBudgetMonthProvider>
    </ChannelProvider>
  );
};

export default BudgetPlanningPage;
